import React, { useEffect } from 'react';
import './App.css';
import { FiCheck } from 'react-icons/fi';

function App() {
  useEffect(() => {
    // Load Fillout script after component mounts
    const script = document.createElement('script');
    script.src = 'https://server.fillout.com/embed/v1/';
    script.async = true;
    script.onload = () => {
      console.log('Fillout script loaded successfully');
      if (window.Fillout) {
        window.Fillout.load();
      }
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen custom-bg">
      {/* Header */}
      <header className="px-4 py-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <img
            src="https://cloud-1de12d.becdn.net/media/original/c03033ce0a627b5cb0683cab6748c293.webp"
            alt="AI Caller Logo"
            className="h-8 lg:h-10"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-sm lg:text-base text-slate-600 mb-4 font-medium tracking-wide">
              The world's first most human AI Sales Agent
            </p>
            
            <h1 className="text-3xl lg:text-5xl xl:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Let AI keep your sales calendar full of <br className="hidden lg:block" />
              <span className="text-indigo-600">qualified B2B meetings</span>
            </h1>
            
            <p className="text-base lg:text-lg text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              We help B2B companies generate predictable, qualified meetings without hiring additional sales reps or increasing ad spend.
            </p>

            {/* Waveform Demo Section */}
            <div className="relative mb-12 lg:mb-16">
              <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 mx-auto max-w-2xl border border-slate-200">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center mr-3 shadow-md">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <span className="text-slate-800 font-semibold text-lg">AI Voice Demo</span>
                </div>

                {/* Waveform Visualization */}
                <div className="relative h-32 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl overflow-hidden border border-slate-100">
                  <div className="absolute inset-0 flex items-center justify-center space-x-1 px-4 opacity-50">
                    {[...Array(50)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-gradient-to-t from-blue-400 to-indigo-500 rounded-full animate-pulse"
                        style={{
                          width: '4px',
                          height: `${Math.random() * 60 + 20}%`,
                          animationDelay: `${i * 0.05}s`,
                          animationDuration: `${0.8 + Math.random()}s`
                        }}
                      />
                    ))}
                  </div>

                  {/* Blur Overlay & Text */}
                  <div className="absolute inset-0 backdrop-blur-sm bg-white/40 flex items-center justify-center">
                    <div className="px-6 py-3 bg-white/90 rounded-full shadow-sm border border-white/50">
                      <p className="text-base font-semibold text-slate-700 tracking-wide">
                        Demo available soon.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              {/* Early Adopter Section */}
              <div className="p-6 lg:p-8 border-b border-slate-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                <h2 style={{ textAlign: 'center' }} className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                  Become an early adopter of our AI SDR – before everyone else uses it.
                </h2>
                
                <p className="text-base text-slate-700 mb-4 leading-relaxed">
                  We're building an AI Sales Rep that automatically books qualified meetings for B2B companies.
                </p>
                
                <p className="text-base text-slate-700 mb-4 leading-relaxed">
                  Before we launch publicly, we're looking for 100 companies who:
                </p>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <FiCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">already do outbound or want to start,</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <FiCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">want a predictable pipeline of qualified meetings,</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <FiCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">and are willing to give us honest feedback.</span>
                  </li>
                </ul>
                
                <p className="text-base font-semibold text-slate-900 mb-3">
                  As an early adopter you will get:
                </p>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <FiCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">Priority access to the first version</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <FiCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">A direct say in the roadmap (we build around your use cases)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <FiCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">Special prelaunch pricing that won't be available after launch</span>
                  </li>
                </ul>
                
                <p className="text-base text-slate-700 leading-relaxed">
                  Join the waitlist to be notified when early access slots open, and tell us in a 2‑minute survey who you are and how you sell today.
                </p>
              </div>

              <div className="p-6 lg:p-8">
                {/* Embedded Fillout Form */}
                <div
                  style={{ width: '100%' }}
                  data-fillout-id="gKopMHANpfus"
                  data-fillout-embed-type="standard"
                  data-fillout-inherit-parameters
                  data-fillout-dynamic-resize
                />
              </div>
            </div>

            <p className="text-center text-slate-600 mt-8 text-sm lg:text-base leading-relaxed">
              Don't miss the next generation of Sales. Register your interest and get every update direct in your pocket.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-8 lg:px-8 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-slate-500">
            R&R Online Group LLC 2026 - All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;